export interface UICitation {
  id: string;
  title: string;
  source: string;
  author?: string;
  sourceType?: string;
  url: string;
  dateAdded: Date;
  usedIn: string[];
  lastUsed: Date;
  note?: string;
}

const SOURCE_TYPE_VALUES = new Set(['url', 'tab', 'document', 'article', 'manual']);

function isSourceTypeLabel(value: string): boolean {
  return SOURCE_TYPE_VALUES.has(value.toLowerCase().trim());
}

export function siteNameFromUrl(url: string): string {
  if (!url) return '';
  try {
    const host = new URL(url).hostname.replace(/^www\./i, '');
    const base = host.split('.')[0] ?? host;
    if (!base) return host;
    return base.charAt(0).toUpperCase() + base.slice(1);
  } catch {
    return '';
  }
}

function formatAccessDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/** APA 7-style reference for web pages (suitable for papers and reports). */
export function formatCitationAPA(citation: UICitation): string {
  const title = citation.title.trim() || 'Untitled source';
  const site = siteNameFromUrl(citation.url);
  const accessed = formatAccessDate(citation.dateAdded);
  const author = citation.author?.trim();

  const lines: string[] = [];

  if (author) {
    lines.push(`${author}. (${citation.dateAdded.getFullYear()}). ${title}.`);
  } else {
    lines.push(`${title}. (n.d.).`);
  }

  if (site) {
    lines.push(`${site}.`);
  }

  if (citation.url) {
    lines.push(`Retrieved ${accessed}, from ${citation.url}`);
  }

  if (citation.note?.trim()) {
    lines.push(`Excerpt: "${citation.note.trim()}"`);
  }

  return lines.join(' ');
}

/** Vancouver-style numbered reference (common in medical literature). */
export function formatCitationVancouver(citation: UICitation, index?: number): string {
  const prefix = index != null ? `${index}. ` : '';
  const title = citation.title.trim() || 'Untitled source';
  const site = siteNameFromUrl(citation.url);
  const author = citation.author?.trim();
  const year = citation.dateAdded.getFullYear();

  const parts: string[] = [prefix];
  if (author) {
    parts.push(`${author}.`);
  }
  parts.push(`${title}.`);
  if (site) {
    parts.push(`${site};`);
  }
  parts.push(`${year}.`);
  if (citation.url) {
    parts.push(`Available from: ${citation.url}`);
  }
  return parts.join(' ');
}

/** Plain readable citation for quick sharing. */
export function formatCitationReadable(citation: UICitation): string {
  const title = citation.title.trim() || 'Untitled source';
  const author = citation.author?.trim();
  const site = siteNameFromUrl(citation.url);
  const bits = [author, `"${title}"`, site, citation.url].filter(Boolean);
  return bits.join(' — ');
}

export function normalizeCitation(raw: Record<string, unknown>): UICitation {
  const usedIn = Array.isArray(raw.usedIn)
    ? raw.usedIn.map(String)
    : raw.used_in
      ? [String(raw.used_in)]
      : [];
  const dateAdded = new Date(String(raw.dateAdded ?? raw.created_at ?? raw.date_added ?? Date.now()));
  const lastUsed = new Date(String(raw.lastUsed ?? raw.last_used ?? dateAdded));
  const sourceType = String(raw.source_type ?? raw.sourceType ?? '').trim() || undefined;
  const rawSource = String(raw.source ?? raw.context ?? '').trim();
  const explicitAuthor = raw.author ? String(raw.author).trim() : '';

  let author = explicitAuthor;
  if (!author && rawSource && !isSourceTypeLabel(rawSource)) {
    author = rawSource;
  }

  const source = sourceType ?? (rawSource || 'manual');

  return {
    id: String(raw.id ?? crypto.randomUUID()),
    title: String(raw.title ?? raw.source_title ?? 'Untitled'),
    source,
    author: author || undefined,
    sourceType,
    url: String(raw.url ?? raw.source_url ?? ''),
    dateAdded,
    lastUsed,
    usedIn,
    note: raw.note ? String(raw.note) : raw.quote ? String(raw.quote) : undefined,
  };
}
